import React, { FormEvent, useContext } from 'react'
import { colors, space } from '@entur/tokens'
import { ButtonGroup, IconButton, PrimaryButton, SecondaryButton } from '@entur/button'
import { EditIcon } from '@entur/icons'
import { GridContainer, GridItem } from '@entur/grid'
import { SmallAlertBox } from '@entur/alert'
import { Heading4 } from '@entur/typography'
import { trackEvent } from '../../utils/tracking-utils'
import { DisabledContext } from '../../contexts/disable-context'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'

type EmployeeEditCardProps = {
  header: string
  onAbort: () => void
  onSubmit: (e: FormEvent) => void
  isLoading: boolean
  valid: boolean
  error?: React.ReactNode
  children: React.ReactNode
}

export const EmployeeEditCard: React.FC<EmployeeEditCardProps> = ({
  header,
  onAbort,
  onSubmit,
  isLoading,
  valid,
  error,
  children,
}) => {
  const abort = (e: FormEvent) => {
    e.preventDefault()
    onAbort()
  }

  return (
    <form onSubmit={onSubmit} onReset={abort}>
      <Heading4 style={{ marginTop: 0, marginBottom: space.large }}>{header}</Heading4>
      <GridContainer
        spacing="medium"
        style={{
          border: `1px solid ${colors.brand.lavender}`,
          padding: space.small,
        }}
      >
        {children}
      </GridContainer>
      <div style={{ marginTop: space.large }}>
        {error && <div>{error}</div>}
        {!valid && (
          <SmallAlertBox variant="error" width="fit-content">
            Alle felt må være fylt ut med gyldige verdier for å sende inn skjemaet.
          </SmallAlertBox>
        )}
        <ButtonGroup>
          <PrimaryButton data-cy="card-save-button" type="submit" loading={isLoading} disabled={isLoading}>
            Lagre rettighetseier
          </PrimaryButton>
          <SecondaryButton data-cy="card-discard-button" type="reset" disabled={isLoading}>
            Forkast endringer
          </SecondaryButton>
        </ButtonGroup>
      </div>
    </form>
  )
}

type EmployeeCardProps = {
  header: string
  onEdit: () => void
  onAbort: () => void
  onSubmit: (e: FormEvent) => void
  isLoading: boolean
  valid: boolean
  error?: React.ReactNode
  editMode: boolean
  children: React.ReactNode
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  header,
  onEdit,
  onAbort,
  onSubmit,
  isLoading,
  valid,
  error,
  editMode,
  children,
}) => {
  const { disabled } = useContext(DisabledContext)
  const { readOnly } = useContext(ReadOnlyContext)
  const { hasSensitiveAccess } = useClientInfo()

  const editButton = !readOnly && hasSensitiveAccess === true && (
    <IconButton
      onClick={() => {
        trackEvent('Open-Edit', { category: 'Employee' })
        onEdit()
      }}
      style={{ float: 'left', border: `1px solid ${colors.blues.blue60}` }}
      size="medium"
      disabled={disabled || readOnly}
      data-cy="edit-icon-button"
    >
      <EditIcon />
    </IconButton>
  )

  return editMode ? (
    <EmployeeEditCard
      header={header}
      onAbort={onAbort}
      onSubmit={onSubmit}
      data-cy="employee-edit-card"
      error={error}
      valid={valid}
      isLoading={isLoading}
    >
      {children}
    </EmployeeEditCard>
  ) : (
    <GridContainer spacing="medium">
      <GridItem small={12} data-cy="employee-card">
        <Heading4 style={{ float: 'left', marginTop: 0, marginRight: space.medium }}>{header}</Heading4>
        {editButton}
      </GridItem>
      {children}
    </GridContainer>
  )
}
